import React, { useEffect, useRef, useState } from 'react';
import { Spin, Alert, Button } from 'antd';
import { message } from 'antd';
import { getEditorConfig } from './documentsApi';
import useOnlyOfficeScript from './useOnlyOfficeScript';
import useOoPluginBridge from '../../hooks/useOoPluginBridge';

export default function DocumentEditor({ docId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onlyOfficeUrl, setOnlyOfficeUrl] = useState(null);
  const [config, setConfig] = useState(null);
  const editorRef = useRef(null);
  const containerRef = useRef(null);

  const { loaded: scriptLoaded, error: scriptError, retry: retryScript } = useOnlyOfficeScript(onlyOfficeUrl);

  // Auth + narrow API bridge for the OnlyOffice Enhanced Paste plugin
  // (dictation, and rich paste from the old PACS).
  useOoPluginBridge(docId);

  // Fetch editor config from backend
  useEffect(() => {
    if (!docId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    getEditorConfig(docId)
      .then((res) => {
        if (cancelled) return;
        const { config: editorConfig, onlyOfficeUrl: url } = res.data.data;
        setConfig(editorConfig);
        setOnlyOfficeUrl(url);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Failed to load editor configuration');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [docId]);

  // Initialize editor once script is loaded and config is ready
  useEffect(() => {
    if (!scriptLoaded || !config || !containerRef.current) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      try {
        editorRef.current = new window.DocsAPI.DocEditor('onlyoffice-editor-container', {
          ...config,
          events: {
            ...config.events,
            onDocumentReady: () => {
              setLoading(false);
            },
            onError: (event) => {
              console.error('OnlyOffice editor error:', event);
              message.error('Editor encountered an error');
            },
            onRequestClose: () => {
              if (onClose) onClose();
            },
          },
        });
      } catch (err) {
        console.error('Failed to initialize OnlyOffice editor:', err);
        setError('Failed to initialize editor');
        setLoading(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [scriptLoaded, config, onClose]);

  // Cleanup editor on unmount
  useEffect(() => {
    return () => {
      if (editorRef.current) {
        try {
          editorRef.current.destroyEditor();
        } catch (e) {
          // Editor may already be destroyed
        }
        editorRef.current = null;
      }
    };
  }, []);

  // Warn on unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (editorRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  if (scriptError) {
    return (
      <div className="flex h-full items-center justify-center">
        <Alert
          type="error"
          message="Failed to load editor"
          description={scriptError}
          action={<Button onClick={retryScript}>Retry</Button>}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <Alert
          type="error"
          message="Editor Error"
          description={error}
          action={
            <div className="flex gap-2">
              <Button onClick={() => { setError(null); setLoading(true); setConfig(null); setOnlyOfficeUrl(null); }}>
                Retry
              </Button>
              {onClose && <Button onClick={onClose}>Close</Button>}
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30">
          <Spin size="large" tip="Loading editor..." />
        </div>
      )}
      <div
        id="onlyoffice-editor-container"
        ref={containerRef}
        className="h-full w-full"
      />
    </div>
  );
}
